import { isPlatformBrowser } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from "@angular/core";
import { MainState } from "@app/store/state";
import { ImageAlias } from "@core/enums/image-alias.enum";
import { ImageInterface } from "@core/interfaces/image.interface";
import { CommonApiService } from "@core/services/api/classic/common/common-api.service";
import { AuthService } from "@core/services/auth.service";
import { DeviceService } from "@core/services/device.service";
import { ImageService } from "@core/services/image/image.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { environment } from "@env/environment";
import { selectCurrentUser } from "@features/account/store/auth.selectors";
import { ForumHtmlSanitizerService } from "@features/forum/services/forum-html-sanitizer.service";
import OfficePaste from "@intevation/tiptap-extension-office-paste";
import { NgbModal, NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { FieldType } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { ConfirmationDialogComponent } from "@shared/components/misc/confirmation-dialog/confirmation-dialog.component";
import { Constants } from "@shared/constants";
import { TipTapImageSource } from "@shared/enums/tiptap-image-source.enum";
import { Editor, Extension, generateHTML, mergeAttributes } from "@tiptap/core";
import CharacterCount from "@tiptap/extension-character-count";
import Dropcursor from "@tiptap/extension-dropcursor";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import TextStyle from "@tiptap/extension-text-style";
import Typography from "@tiptap/extension-typography";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import { EMPTY, firstValueFrom, Subject, Subscription } from "rxjs";
import { catchError, debounceTime, map, switchMap, takeUntil } from "rxjs/operators";

import { FontSize } from "./font-size.extension";
import { hasMathContent, loadMathExtension } from "./lazy-math.extension";

@Component({
  selector: "astrobin-formly-field-tiptap",
  templateUrl: "./formly-field-tiptap.component.html",
  styleUrls: ["./formly-field-tiptap.component.scss"]
})
export class FormlyFieldTiptapComponent extends FieldType implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("editorElement") editorElement?: ElementRef<HTMLElement>;
  @ViewChild("emojiPicker") emojiPickerElement?: ElementRef<HTMLElement>;
  @ViewChild("linkUrlInput") linkUrlInput?: ElementRef<HTMLInputElement>;

  editor?: Editor;
  isLoading = true;
  isUploading = false;
  moreOptionsDropdown = false;
  showEmojiPicker = false;
  showSymbolPicker = false;
  showImageSourceTooltip = false;
  showLinkTooltip = false;
  linkUrl = "";
  linkText = "";
  isFullscreen = false;
  isDraggingOver = false;
  currentCharacterCount = 0;
  currentByteCount = 0;
  readonly ImageSource = TipTapImageSource;
  readonly symbolCategories = [
    {
      name: "Basic",
      symbols: [
        "!",
        "#",
        "$",
        "%",
        "&",
        "'",
        "(",
        ")",
        "*",
        "+",
        "-",
        ".",
        "/",
        ":",
        ";",
        "<",
        "=",
        ">",
        "?",
        "@",
        "[",
        "]",
        "^",
        "_",
        "`",
        "{",
        "|",
        "}",
        "~"
      ]
    },
    {
      name: "Currency",
      symbols: ["€", "£", "¢", "¥", "¤", "§"]
    },
    {
      name: "Math",
      symbols: [
        "×",
        "÷",
        "±",
        "≈",
        "≠",
        "≤",
        "≥",
        "°",
        "²",
        "³",
        "µ",
        "¶",
        "∞",
        "√",
        "∑",
        "∏",
        "∫",
        "∂",
        "∆",
        "∇",
        "∈",
        "∉",
        "⊂",
        "⊃",
        "∪",
        "∩"
      ]
    },
    {
      name: "Fractions",
      symbols: ["¼", "½", "¾", "⅓", "⅔", "⅛", "⅜", "⅝", "⅞"]
    },
    {
      name: "Symbols",
      symbols: [
        "©",
        "®",
        "™",
        "«",
        "»",
        '"',
        "'",
        "…",
        "•",
        "→",
        "←",
        "↑",
        "↓",
        "⇒",
        "⇔",
        "♦",
        "♠",
        "♣",
        "♥",
        "★",
        "☆",
        "☑",
        "☐",
        "◆",
        "◇"
      ]
    },
    {
      name: "Latin",
      symbols: [
        "À",
        "Á",
        "Â",
        "Ã",
        "Ä",
        "Å",
        "Æ",
        "Ç",
        "È",
        "É",
        "Ê",
        "Ë",
        "Ì",
        "Í",
        "Î",
        "Ï",
        "Ñ",
        "Ò",
        "Ó",
        "Ô",
        "Õ",
        "Ö",
        "Ø",
        "Ù",
        "Ú",
        "Û",
        "Ü",
        "Ý",
        "à",
        "á",
        "â",
        "ã",
        "ä",
        "å",
        "æ",
        "ç",
        "è",
        "é",
        "ê",
        "ë",
        "ì",
        "í",
        "î",
        "ï",
        "ñ",
        "ò",
        "ó",
        "ô",
        "õ",
        "ö",
        "ø",
        "ù",
        "ú",
        "û",
        "ü",
        "ý",
        "ÿ",
        "Œ",
        "œ",
        "Ÿ",
        "ß"
      ]
    }
  ];
  private _uploadErrorToastId?: number;
  private readonly _destroyed$ = new Subject<void>();
  private _isUpdatingFromValueChange = false;
  private _currentPlaceholder: string;
  private _emojiPicker: any;
  private _searchUsersSubject = new Subject<{ searchTerm: string; renderList: any }>();
  private _searchUsersSubscription: Subscription;
  private _currentMentionResolve: any;
  private _mathExtensionLoaded = false;
  private _mathExtensionLoading = false;
  private _mathExtension: any = null;

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: Object,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly utilsService: UtilsService,
    private readonly translateService: TranslateService,
    private readonly _commonApiService: CommonApiService,
    private readonly _popNotificationsService: PopNotificationsService,
    private readonly _http: HttpClient,
    private readonly _authService: AuthService,
    private readonly _offcanvasService: NgbOffcanvas,
    private readonly _modalService: NgbModal,
    private readonly _deviceService: DeviceService,
    private readonly _imageService: ImageService,
    private readonly _store$: Store<MainState>,
    private readonly _forumHtmlSanitizer: ForumHtmlSanitizerService,
    private readonly _windowRef: WindowRefService
  ) {
    super();
    // Set up debounced search for users
    this._setupDebouncedUserSearch();
  }

  get placeholder(): string {
    return this._currentPlaceholder || this.field?.props?.placeholder || "";
  }

  get readOnly(): boolean {
    return this.formControl?.disabled || false;
  }

  get height(): string {
    return this.field?.props?.height || "300px";
  }

  get bytePercentage(): number {
    if (!this.field?.props?.maxLength) {
      return 0;
    }
    return (this.currentByteCount / this.field.props.maxLength) * 100;
  }

  get shouldShowByteCount(): boolean {
    return this.bytePercentage > 80;
  }

  get byteCountClass(): string {
    if (this.bytePercentage > 100) {
      return "byte-count-over-limit";
    } else if (this.bytePercentage > 90) {
      return "byte-count-danger";
    } else if (this.bytePercentage > 80) {
      return "byte-count-warning";
    }
    return "";
  }

  get byteCountText(): string {
    const percentage = this.bytePercentage.toFixed(1);
    return this.translateService.instant("{{0}}% of limit", { 0: percentage });
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdown = target.closest(".font-size-dropdown");
    const emojiPicker = target.closest("emoji-picker");
    const emojiButton = target.closest(".emoji-button");
    const imageTooltip = target.closest(".image-source-tooltip");
    const imageButton = target.closest(".image-button-container");
    const linkTooltip = target.closest(".link-tooltip, .link-tooltip-dropdown");
    const linkButton = target.closest(".link-button-container");
    const linkDropdownButton = target.closest(".dropdown-item") && target.textContent?.includes("Insert link");

    // Check if we clicked on a link inside the TipTap editor
    if (target.tagName === "A" && target.closest(".ProseMirror") && this.editor) {
      event.preventDefault();
      event.stopPropagation();

      // Find the position and set selection inside the link
      const view = this.editor.view;
      const pos = view.posAtDOM(target, 0);

      // Set cursor inside the link and extend to select the entire link
      this.editor
        .chain()
        .focus()
        .setTextSelection(pos) // Start at the beginning of the link
        .extendMarkRange("link")
        .run();

      // Now open the link tooltip which will detect the link properly
      this.utilsService.delay(10).subscribe(() => {
        this.toggleLinkTooltip();
      });

      return;
    }

    if (!dropdown) {
      this.moreOptionsDropdown = false;
    }

    // Close emoji picker if clicking anywhere except on the emoji picker itself or the button
    if (this.showEmojiPicker && !emojiPicker && !emojiButton) {
      this.showEmojiPicker = false;
    }

    // Close image source tooltip if clicking anywhere except on the tooltip or button
    if (this.showImageSourceTooltip && !imageTooltip && !imageButton) {
      this.showImageSourceTooltip = false;
    }

    // Close link tooltip if clicking anywhere except on the tooltip or button
    if (this.showLinkTooltip && !linkTooltip && !linkButton && !linkDropdownButton) {
      this.showLinkTooltip = false;
    }
  }

  @HostListener("document:keydown.escape", ["$event"])
  onEscapeKey(event: KeyboardEvent): void {
    // If the event has already been handled, don't process it again
    if (event.defaultPrevented) {
      return;
    }

    // Check if the escape key was pressed inside a link tooltip input
    const target = event.target as HTMLElement;
    const isInLinkTooltip = target.closest(".link-tooltip, .link-tooltip-dropdown");

    if (isInLinkTooltip && this.showLinkTooltip) {
      // Let the input's own escape handler handle it
      return;
    }

    if (this.showEmojiPicker) {
      this.showEmojiPicker = false;
      event.preventDefault();
      return;
    }
    if (this.showSymbolPicker) {
      this.showSymbolPicker = false;
      event.preventDefault();
      return;
    }
    if (this.moreOptionsDropdown) {
      this.moreOptionsDropdown = false;
      event.preventDefault();
      return;
    }
    if (this.showImageSourceTooltip) {
      this.showImageSourceTooltip = false;
      event.preventDefault();
      return;
    }
    if (this.showLinkTooltip) {
      this.showLinkTooltip = false;
      this.linkUrl = "";
      this.linkText = "";
      event.preventDefault();
      this._changeDetectorRef.detectChanges();
      return;
    }
    // Only exit fullscreen if no other popups are open
    if (this.isFullscreen) {
      this.isFullscreen = false;
      event.preventDefault();
      this._changeDetectorRef.markForCheck();
    }
  }

  ngOnInit(): void {
    // Initialize placeholder
    this._selectRandomPlaceholder();
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this._platformId) && this.editorElement) {
      // Ensure the DOM is ready
      this.utilsService.delay(1).subscribe(() => {
        void this._initializeEditor();
      });
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    if (this.editor) {
      this.editor.destroy();
    }
    if (this._searchUsersSubscription) {
      this._searchUsersSubscription.unsubscribe();
    }
    this._searchUsersSubject.complete();
  }

  applyFormat(format: string): void {
    if (!this.editor) {
      return;
    }

    switch (format) {
      case "bold":
        this.editor.chain().focus().toggleBold().run();
        break;
      case "italic":
        this.editor.chain().focus().toggleItalic().run();
        break;
      case "strike":
        this.editor.chain().focus().toggleStrike().run();
        break;
      case "bulletList":
        this.editor.chain().focus().toggleBulletList().run();
        break;
      case "orderedList":
        this.editor.chain().focus().toggleOrderedList().run();
        break;
      case "clean":
        this.editor.chain().focus().unsetAllMarks().run();
        break;
    }
  }

  toggleFontSizeDropdown(): void {
    this.moreOptionsDropdown = !this.moreOptionsDropdown;
  }

  getCurrentFontSize(): string {
    if (!this.editor) {
      return "Medium";
    }

    const attrs = this.editor.getAttributes("textStyle");
    if (attrs?.fontSize === "75%") {
      return "Small";
    } else if (attrs?.fontSize === "150%") {
      return "Large";
    }
    return "Medium";
  }

  applyFontSize(size: "small" | "medium" | "large"): void {
    if (!this.editor) {
      return;
    }

    this.moreOptionsDropdown = false;

    switch (size) {
      case "small":
        this.editor.commands.setFontSize("75%");
        break;
      case "medium":
        this.editor.commands.unsetFontSize();
        break;
      case "large":
        this.editor.commands.setFontSize("150%");
        break;
    }
  }

  clearFormatting(): void {
    if (!this.editor) {
      return;
    }

    this.moreOptionsDropdown = false;
    this.editor.chain().focus().unsetAllMarks().run();
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.moreOptionsDropdown = false;
    this._changeDetectorRef.markForCheck();
  }

  applyFormatFromDropdown(format: string): void {
    this.moreOptionsDropdown = false;
    this.applyFormat(format);
  }

  toggleLinkFromDropdown(): void {
    this.moreOptionsDropdown = false;
    this._changeDetectorRef.detectChanges();
    // Small delay to ensure dropdown closes before tooltip opens
    this.utilsService.delay(10).subscribe(() => {
      this.toggleLinkTooltip();
      this._changeDetectorRef.detectChanges();
    });
  }

  toggleSymbolFromDropdown(): void {
    this.moreOptionsDropdown = false;
    this._changeDetectorRef.detectChanges();
    // Small delay to ensure dropdown closes before symbol picker opens
    this.utilsService.delay(10).subscribe(() => {
      this.toggleSymbolPicker();
      this._changeDetectorRef.detectChanges();
    });
  }

  isFormatActive(format: string): boolean {
    if (!this.editor) {
      return false;
    }

    switch (format) {
      case "bold":
        return this.editor.isActive("bold");
      case "italic":
        return this.editor.isActive("italic");
      case "strike":
        return this.editor.isActive("strike");
      case "bulletList":
        return this.editor.isActive("bulletList");
      case "orderedList":
        return this.editor.isActive("orderedList");
      default:
        return false;
    }
  }

  /**
   * Insert a quote block into the editor
   * @param post The post to quote
   * @param contentFormat The format of the content ('html', 'json', 'bbcode', or null)
   * @param currentPage The current page number (for post URL)
   */
  async insertQuote(
    post: {
      id: number;
      topic: number;
      username: string;
      userDisplayName?: string;
      displayName?: string;
      created: string;
      body: string;
      bodyHtml?: string;
      bodyHtmlPrecise?: string;
    },
    contentFormat: string | null,
    currentPage?: number,
    selectedText?: string
  ): Promise<void> {
    if (!this.editor) {
      return;
    }

    if (!post) {
      return;
    }

    // Get the author name
    const authorName = post.displayName || post.userDisplayName || post.username;

    // Format the timestamp
    const dateStr = new Date(post.created).toLocaleString("en", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });

    // Build the URLs with absolute paths
    const origin =
      isPlatformBrowser(this._platformId) && this._windowRef.nativeWindow
        ? this._windowRef.nativeWindow.location.origin
        : environment.baseUrl;
    const userUrl = `${origin}/u/${post.username}`;
    const postUrl = `${origin}/forum/topic/${post.topic}?page=${currentPage || 1}#post-${post.id}`;

    // Build the complete content first
    let quotedContent = "";

    // If selectedText is provided, use it directly
    if (selectedText) {
      quotedContent = selectedText;
    } else {
      // Otherwise, use the full post content based on format
      if (contentFormat === "json") {
        try {
          const parsedContent = JSON.parse(post.body);

          // Check if content has math and load extension if needed
          const contentText = this._extractTextFromContent(parsedContent);
          if (hasMathContent(contentText) && !this._mathExtensionLoaded) {
            await this._loadMathExtension();
          }

          // Convert JSON to HTML using generateHTML function
          const extensions = [
            StarterKit.configure({
              heading: false,
              codeBlock: false,
              horizontalRule: false
            }),
            Dropcursor.configure({
              color: "#007bff",
              width: 2
            }),
            Link.configure({
              openOnClick: false,
              autolink: true,
              linkOnPaste: true,
              HTMLAttributes: {
                rel: "noopener noreferrer"
              }
            }),
            TextStyle,
            FontSize,
            Image.configure({
              inline: true,
              allowBase64: false,
              HTMLAttributes: {
                class: "editor-image"
              }
            }),
            Mention.configure({
              HTMLAttributes: {
                class: "mention"
              },
              renderHTML({ node }) {
                return [
                  "a",
                  mergeAttributes(
                    { class: "mention" },
                    {
                      href: `/users/${node.attrs.id}/`,
                      "data-mention": node.attrs.id,
                      "data-username": node.attrs.id,
                      "data-display-name": node.attrs.label
                    }
                  ),
                  `@${node.attrs.label}`
                ];
              }
            })
          ];

          // Add math extension if loaded
          if (this._mathExtensionLoaded && this._mathExtension) {
            extensions.push(this._mathExtension);
          }

          quotedContent = generateHTML(parsedContent, extensions);
        } catch (e) {
          quotedContent = `<p>${post.body}</p>`;
        }
      } else {
        quotedContent =
          contentFormat === "bbcode" ? post.bodyHtmlPrecise || post.bodyHtml || post.body : post.bodyHtml || post.body;
      }
    }

    // Clean up quotedContent - remove empty paragraphs and trim whitespace
    quotedContent = quotedContent
      .replace(/<p>\s*<\/p>/g, "") // Remove empty paragraphs
      .replace(/<p><br\s*\/?>\s*<\/p>/g, "") // Remove paragraphs with only br
      .trim();

    // When we have JSON content with math nodes, we need to insert it differently
    if (contentFormat === "json" && !selectedText) {
      try {
        const parsedContent = JSON.parse(post.body);

        // Check if content has math nodes and ensure math extension is loaded
        const hasMathNodes = this._contentHasMathNodes(parsedContent);
        if (hasMathNodes && !this._mathExtensionLoaded) {
          await this._loadMathExtension();
          // After loading the extension, we need to reconfigure the editor
          if (this._mathExtension) {
            // Add the math extension to the editor
            const currentExtensions = this.editor.extensionManager.extensions;
            const hasMathjax = currentExtensions.some(ext => ext.name === "mathjax");
            if (!hasMathjax) {
              // We need to recreate the editor with the math extension
              // For now, convert to HTML as a fallback
              quotedContent = generateHTML(parsedContent, [...currentExtensions, this._mathExtension]);
              const fullHtml = `<blockquote><p><a href="${userUrl}" target="_blank" class="forum-quote-author">${authorName}</a> · <a href="${postUrl}" target="_blank" class="forum-quote-timestamp">${dateStr}</a></p>${quotedContent}</blockquote><p></p>`;
              this.editor.chain().focus("end").insertContent(fullHtml).run();
              return;
            }
          }
        }

        // Build the quote structure in JSON format to preserve math nodes
        const quoteJson = {
          type: "blockquote",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: authorName,
                  marks: [
                    {
                      type: "link",
                      attrs: {
                        href: userUrl,
                        target: "_blank",
                        class: "forum-quote-author"
                      }
                    }
                  ]
                },
                {
                  type: "text",
                  text: " · "
                },
                {
                  type: "text",
                  text: dateStr,
                  marks: [
                    {
                      type: "link",
                      attrs: {
                        href: postUrl,
                        target: "_blank",
                        class: "forum-quote-timestamp"
                      }
                    }
                  ]
                }
              ]
            },
            // Add the original content nodes
            ...parsedContent.content
          ]
        };

        // Insert the quote as JSON to preserve math nodes
        this.editor
          .chain()
          .focus("end")
          .insertContent([quoteJson, { type: "paragraph" }])
          .run();
        return;
      } catch (e) {
        // Fall back to HTML insertion if JSON parsing fails
      }
    }

    // For non-JSON content or selected text, use HTML insertion
    const fullHtml = `<blockquote><p><a href="${userUrl}" target="_blank" class="forum-quote-author">${authorName}</a> · <a href="${postUrl}" target="_blank" class="forum-quote-timestamp">${dateStr}</a></p>${quotedContent}</blockquote><p></p>`;

    // Move cursor to the end and insert the HTML
    this.editor.chain().focus("end").insertContent(fullHtml).run();
  }

  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
    this._changeDetectorRef.detectChanges();

    if (this.showEmojiPicker) {
      if (!this._emojiPicker) {
        // Wait for the emoji picker container to be rendered
        this.utilsService.delay(1).subscribe(() => {
          void this._initializeEmojiPicker();
        });
      } else {
        // If already initialized, just ensure it's visible
        this._changeDetectorRef.markForCheck();
      }
    }
  }

  toggleSymbolPicker(): void {
    this.showSymbolPicker = !this.showSymbolPicker;
    this._changeDetectorRef.markForCheck();
  }

  insertSymbol(symbol: string): void {
    if (!this.editor) {
      return;
    }

    this.editor.chain().focus().insertContent(symbol).run();
    this.showSymbolPicker = false;
    this._changeDetectorRef.markForCheck();
  }

  insertMath(): void {
    if (!this.editor) {
      return;
    }

    // Close the dropdown
    this.moreOptionsDropdown = false;

    // Load math extension if not already loaded
    void this._loadMathExtension().then(() => {
      // Insert a math delimiter to start typing an equation
      this.editor.chain().focus().insertContent("\\(").run();

      // Show a hint to the user
      this._popNotificationsService.info(
        this.translateService.instant(
          "Type your LaTeX equation between \\(...\\) for inline or \\[...\\] for centered block.<br>• Inline: <code>\\(x + y = z\\)</code><br>• Block: <code>\\[\\int_0^\\infty e^{-x} dx\\]</code>"
        ),
        null,
        { enableHtml: true }
      );
    });
  }

  toggleImageSourceTooltip(): void {
    this.showImageSourceTooltip = !this.showImageSourceTooltip;
    this._changeDetectorRef.markForCheck();
  }

  handleImageSourceSelection(source: TipTapImageSource): void {
    this.showImageSourceTooltip = false;

    if (source === TipTapImageSource.UPLOAD) {
      this.uploadImage();
    } else if (source === TipTapImageSource.GALLERY) {
      void this.selectImageFromGallery();
    }
  }

  toggleLinkTooltip(): void {
    this.showLinkTooltip = !this.showLinkTooltip;
    if (this.showLinkTooltip) {
      this.linkUrl = "";
      this.linkText = "";

      const linkAttrs = this.editor.getAttributes("link");
      const isEditingLink = this.editor.isActive("link") && linkAttrs.href;

      if (isEditingLink) {
        this.linkUrl = linkAttrs.href || "";

        const { from, to } = this.editor.state.selection;
        const text = this.editor.state.doc.textBetween(from, to);

        // For autodetected links (where text and URL are the same), leave text field empty
        // so the user can optionally provide a different display text
        if (text === linkAttrs.href) {
          this.linkText = "";
        } else {
          this.linkText = text;
        }
      } else {
        const { from, to } = this.editor.state.selection;
        const selectedText = this.editor.state.doc.textBetween(from, to);
        this.linkText = selectedText || "";
      }

      this._changeDetectorRef.markForCheck();

      this.utilsService.delay(1).subscribe(() => {
        if (this.linkUrlInput?.nativeElement) {
          this.linkUrlInput.nativeElement.focus();
        }
      });
    }
  }

  closeLinkTooltip(event?: Event): void {
    this.showLinkTooltip = false;
    this.linkUrl = "";
    this.linkText = "";
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this._changeDetectorRef.detectChanges();
  }

  isEditingLink(): boolean {
    if (!this.editor) {
      return false;
    }

    return this.editor.isActive("link");
  }

  removeLink(): void {
    if (!this.editor) {
      return;
    }

    this.editor.chain().focus().extendMarkRange("link").unsetLink().run();

    this.closeLinkTooltip();
  }

  insertLink(): void {
    if (!this.editor || !this.linkUrl) {
      return;
    }

    // Ensure URL has protocol
    let url = this.linkUrl;
    if (!url.match(/^https?:\/\//)) {
      url = "https://" + url;
    }

    const attrs: any = { href: url };

    const isEditingLink = this.editor.isActive("link");
    const { empty } = this.editor.state.selection;

    if (isEditingLink) {
      const { from, to } = this.editor.state.selection;
      const currentText = this.editor.state.doc.textBetween(from, to);
      const currentLinkAttrs = this.editor.getAttributes("link");

      if (this.linkText && this.linkText !== currentText) {
        this.editor
          .chain()
          .deleteRange({ from, to })
          .insertContent(this.linkText)
          .setTextSelection({ from, to: from + this.linkText.length })
          .setLink(attrs)
          .run();
      } else if (!this.linkText && currentText === currentLinkAttrs.href) {
        // For autodetected links being edited with empty text field
        this.editor
          .chain()
          .deleteRange({ from, to })
          .insertContent(url)
          .setTextSelection({ from, to: from + url.length })
          .setLink(attrs)
          .run();
      } else {
        this.editor.chain().updateAttributes("link", attrs).run();
      }
    } else if (!empty) {
      this.editor.chain().focus().setLink(attrs).run();
    } else {
      const text = this.linkText || this.linkUrl;
      this.editor.chain().focus().insertContent(`<a href="${attrs.href}">${text}</a>`).run();
    }

    this.closeLinkTooltip();
  }

  uploadImage(): void {
    if (!this.editor) {
      return;
    }

    // Clear any existing upload error notification
    if (this._uploadErrorToastId) {
      this._popNotificationsService.remove(this._uploadErrorToastId);
      this._uploadErrorToastId = undefined;
    }

    // Focus the editor first to prevent validation issues
    this.editor.commands.focus();

    // Create hidden file input
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";

    // Handle file selection
    input.addEventListener("change", async (event: any) => {
      const file = event.target.files?.[0];
      if (file && file.type.startsWith("image/")) {
        this.isUploading = true;

        // If editor is empty, add invisible character to prevent validation error
        const wasEmpty = this.editor.isEmpty;
        if (wasEmpty) {
          this.editor.commands.setContent("\u200B"); // Zero-width space
        }

        this.editor.setEditable(false); // Disable editor during upload
        this._changeDetectorRef.markForCheck();

        try {
          const result = await firstValueFrom(this._commonApiService.editorUpload(file));
          if (result.url) {
            if (wasEmpty) {
              this.editor.commands.clearContent();
            }

            this.editor.commands.focus();
            this._insertImage(result.url, file.name);
          }
        } catch (error: any) {
          // Show the error message (already localized from backend or fallback)
          const toast = this._popNotificationsService.error(error.message);
          if (toast) {
            this._uploadErrorToastId = toast.toastId;
          }

          if (wasEmpty) {
            this.editor.commands.clearContent();
          }
        } finally {
          this.isUploading = false;
          this.editor.setEditable(true); // Re-enable editor after upload
          this._changeDetectorRef.markForCheck();
        }
      }
      // Clean up
      document.body.removeChild(input);
    });

    // Add to DOM and trigger click
    document.body.appendChild(input);
    input.click();
  }

  async selectImageFromGallery(): Promise<void> {
    if (!this.editor) {
      return;
    }

    // Dynamically import the gallery selector component
    const { TiptapGalleryImageSelectorComponent } = await import(
      "./tiptap-gallery-image-selector/tiptap-gallery-image-selector.component"
    );

    const offcanvasRef = this._offcanvasService.open(TiptapGalleryImageSelectorComponent, {
      panelClass: "tiptap-gallery-selector-offcanvas",
      position: this._deviceService.offcanvasPosition()
    });

    // Pass the current user ID to the component
    const currentUser = await firstValueFrom(this._store$.select(selectCurrentUser));
    if (currentUser) {
      offcanvasRef.componentInstance.userId = currentUser.id;
    }

    try {
      const selectedImage: ImageInterface = await offcanvasRef.result;
      if (selectedImage) {
        const imageUrl = this._imageService.getThumbnail(selectedImage, ImageAlias.HD);
        // Generate the image link using current domain (SSR-safe)
        const origin =
          isPlatformBrowser(this._platformId) && this._windowRef.nativeWindow
            ? this._windowRef.nativeWindow.location.origin
            : environment.baseUrl;
        const imageLink = `${origin}/i/${selectedImage.hash || selectedImage.pk}/`;
        this._insertImage(imageUrl, selectedImage.title, imageLink);
      }
    } catch {
      // User dismissed the offcanvas without selecting
    }
  }

  focusEditor(): void {
    if (!this.editor) {
      return;
    }
    this.editor.commands.focus();
  }

  clearContent(): void {
    if (!this.editor) {
      return;
    }

    const modalRef = this._modalService.open(ConfirmationDialogComponent);
    const componentInstance = modalRef.componentInstance as ConfirmationDialogComponent;

    componentInstance.title = this.translateService.instant("Clear content");
    componentInstance.message = this.translateService.instant(
      "Are you sure you want to clear all content? This action cannot be undone."
    );
    componentInstance.showAreYouSure = false;
    componentInstance.confirmLabel = this.translateService.instant("Yes, clear");
    componentInstance.cancelLabel = this.translateService.instant("Cancel");
    componentInstance.confirmButtonClass = "btn-danger";

    modalRef.closed.subscribe(() => {
      if (!this.editor) {
        return;
      }

      // Set the flag to prevent infinite loop
      this._isUpdatingFromValueChange = true;

      // Clear the editor content
      this.editor.commands.clearContent();

      // Update the form control
      if (this.formControl) {
        this.formControl.setValue("");
        this.formControl.markAsDirty();
        this.formControl.markAsTouched();
      }

      // Manually reset character and byte counts
      this.currentCharacterCount = 0;
      this.currentByteCount = 0;

      // Reset the flag
      this._isUpdatingFromValueChange = false;

      // Focus the editor
      this.editor.commands.focus();

      this._changeDetectorRef.markForCheck();
    });
  }

  uploadFile(): void {
    if (!this.editor) {
      return;
    }

    // Clear any existing upload error notification
    if (this._uploadErrorToastId) {
      this._popNotificationsService.remove(this._uploadErrorToastId);
      this._uploadErrorToastId = undefined;
    }

    // Focus the editor first to prevent validation issues
    this.editor.commands.focus();

    // Create hidden file input
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "*/*";
    input.style.display = "none";

    // Handle file selection
    input.addEventListener("change", async (event: any) => {
      const file = event.target.files?.[0];
      if (file) {
        this.isUploading = true;

        // If editor is empty, add invisible character to prevent validation error
        const wasEmpty = this.editor.isEmpty;
        if (wasEmpty) {
          this.editor.commands.setContent("\u200B"); // Zero-width space
        }

        this.editor.setEditable(false); // Disable editor during upload
        this._changeDetectorRef.markForCheck();

        try {
          const result = await firstValueFrom(this._commonApiService.editorUpload(file));
          if (result.url) {
            if (wasEmpty) {
              this.editor.commands.clearContent();
            }

            const linkText = file.name;
            this.editor
              .chain()
              .focus()
              .insertContent(
                `<a href="${result.url}" target="_blank" rel="noopener noreferrer" class="tiptap-link">${linkText}</a>`
              )
              .run();
          }
        } catch (error: any) {
          // Show the error message (already localized from backend or fallback)
          const toast = this._popNotificationsService.error(error.message);
          if (toast) {
            this._uploadErrorToastId = toast.toastId;
          }

          if (wasEmpty) {
            this.editor.commands.clearContent();
          }
        } finally {
          this.isUploading = false;
          this.editor.setEditable(true); // Re-enable editor after upload
          this._changeDetectorRef.markForCheck();
        }
      }
      // Clean up
      document.body.removeChild(input);
    });

    // Add to DOM and trigger click
    document.body.appendChild(input);
    input.click();
  }

  private async _initializeEditor(): Promise<void> {
    // Check if initial content has math
    const initialContent = this._getInitialContent();
    const contentText = this._extractTextFromContent(initialContent);

    if (hasMathContent(contentText)) {
      // Load math extension before initializing
      await this._loadMathExtension();
    }

    void this._initializeEditorWithExtensions([]);
  }

  private async _initializeEditorWithExtensions(additionalExtensions: any[] = []): Promise<void> {
    if (!this.editorElement?.nativeElement) {
      console.error("TipTap: Editor element not found");
      return;
    }

    // If math is loaded but not in additional extensions, load it
    if (this._mathExtensionLoaded && additionalExtensions.length === 0) {
      try {
        const mathExtension = await loadMathExtension();
        this._mathExtension = mathExtension; // Store the extension
        additionalExtensions = [mathExtension];
      } catch (error) {
        console.error("Failed to get math extension:", error);
      }
    }

    this.editor = new Editor({
      element: this.editorElement.nativeElement,
      autofocus: this.field?.props?.autofocus || false,
      editorProps: {
        handleDOMEvents: {
          keydown: (view, event) => {
            // Handle Enter key for simplified editor mode
            if (
              this.field?.props?.handleEnterKey &&
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.ctrlKey &&
              !event.metaKey
            ) {
              const handled = this.field.props.handleEnterKey();
              if (handled) {
                event.preventDefault();
                return true;
              }
            }
            return false;
          },
          dragenter: (view, event) => {
            // Check if dragging files (not text)
            if (event.dataTransfer && event.dataTransfer.types.includes("Files")) {
              event.preventDefault();
              this.isDraggingOver = true;
              this._changeDetectorRef.markForCheck();
            }
            return false;
          },
          dragover: (view, event) => {
            // Prevent default to allow drop
            if (event.dataTransfer && event.dataTransfer.types.includes("Files")) {
              event.preventDefault();
              event.dataTransfer.dropEffect = "copy";
            }
            return false;
          },
          dragleave: (view, event) => {
            // Check if we're leaving the editor completely
            const rect = view.dom.getBoundingClientRect();
            const x = event.clientX;
            const y = event.clientY;

            if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
              this.isDraggingOver = false;
              this._changeDetectorRef.markForCheck();
            }
            return false;
          }
        },
        handleDrop: (view, event, slice, moved) => {
          // Reset drag state
          this.isDraggingOver = false;
          this._changeDetectorRef.markForCheck();

          // Handle file drops from external sources (e.g., Finder)
          if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            event.preventDefault();
            event.stopPropagation();

            const files = Array.from(event.dataTransfer.files);

            // Process each dropped file
            files.forEach(file => {
              void this._handleDroppedFile(file);
            });

            return true; // Indicate we handled the drop
          }

          return false; // Let TipTap handle other drops (text, internal content)
        },
        handlePaste: (view, event) => {
          // Check if clipboard has HTML or text content first
          // This prevents treating Word content as image files
          if (event.clipboardData) {
            const hasHtml = event.clipboardData.getData("text/html");
            const hasText = event.clipboardData.getData("text/plain");

            // If there's HTML or text content, let TipTap handle it normally
            if (hasHtml || hasText) {
              return false; // Let TipTap's default paste handling take over
            }

            // Only handle file paste if there's no HTML/text content
            if (event.clipboardData.files && event.clipboardData.files.length > 0) {
              const files = Array.from(event.clipboardData.files);

              // Only handle if there are actual files
              const hasFiles = files.some(file => file.size > 0);
              if (hasFiles) {
                event.preventDefault();

                files.forEach(file => {
                  if (file.size > 0) {
                    void this._handleDroppedFile(file);
                  }
                });

                return true;
              }
            }
          }

          return false;
        },
        transformPastedHTML: html => {
          // Clean up common Office/Google Docs artifacts
          const cleanedHtml = html
            // Remove Office-specific tags and comments
            .replace(/<!--\[if[^>]*>.*?<!\[endif\]-->/gi, "")
            .replace(/<!--.*?-->/g, "")
            .replace(/<o:p\s*\/?>|<\/o:p>/gi, "")
            // Remove style tags
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
            // Remove meta and link tags
            .replace(/<meta[^>]*>/gi, "")
            .replace(/<link[^>]*>/gi, "")
            // Clean up spans with only style attributes (but preserve spans with classes or other attributes)
            .replace(/<span(?:\s+style="[^"]*")?(\s*>)([^<]*)<\/span>/gi, (match, closing, content) => {
              // Check if the span has other attributes besides style
              const hasOtherAttrs = match.match(/\s+(?!style)[a-zA-Z-]+=/);
              return hasOtherAttrs ? match : content;
            })
            // Remove empty paragraphs
            .replace(/<p[^>]*>(?:\s|&nbsp;)*<\/p>/gi, "")
            // Convert multiple nbsp to single space
            .replace(/(&nbsp;)+/g, " ")
            // Remove data-* attributes except those needed for mentions
            .replace(/\s*data-(?!mention|username|display-name)[^=]+="[^"]*"/gi, "")
            // Remove class attributes that are Office-specific
            .replace(/\s*class="(Mso[^"]*|Apple-[^"]*)"/gi, "")
            // Remove font tags
            .replace(/<\/?font[^>]*>/gi, "")
            // Clean up nested divs that only contain text
            .replace(/<div>([^<]+)<\/div>/gi, "<p>$1</p>")
            // Normalize line breaks
            .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, "</p><p>")
            // Clean up extra whitespace
            .replace(/\s+/g, " ")
            .trim();

          // Check if pasted content has math and load extension if needed
          if (hasMathContent(cleanedHtml) && !this._mathExtensionLoaded) {
            void this._loadMathExtension();
          }

          // The ForumHtmlSanitizerService returns SafeHtml, but we need a string for TipTap
          // We'll use its internal sanitization logic directly
          return this._cleanPastedHtml(cleanedHtml);
        }
      },
      extensions: [
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          horizontalRule: false
        }),
        // Only add Dropcursor if math extension is not loaded (it includes its own)
        ...(this._mathExtensionLoaded
          ? []
          : [
              Dropcursor.configure({
                color: "#007bff",
                width: 2
              })
            ]),
        OfficePaste,
        Link.extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              target: {
                default: null
              },
              rel: {
                default: null
              }
            };
          }
        }).configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: {
            class: "tiptap-link"
          }
        }),
        Placeholder.configure({
          placeholder: this.placeholder,
          emptyEditorClass: "is-editor-empty",
          emptyNodeClass: "is-empty"
        }),
        TextStyle,
        FontSize,
        Typography.configure({
          // Typography options
          emDash: "—", // Convert -- to em dash
          ellipsis: "…", // Convert ... to ellipsis
          openDoubleQuote: "\u201C", // Convert " to left double quote
          closeDoubleQuote: "\u201D", // Convert " to right double quote
          openSingleQuote: "\u2018", // Convert ' to left single quote
          closeSingleQuote: "\u2019", // Convert ' to right single quote
          leftArrow: "←", // Convert <- to left arrow
          rightArrow: "→", // Convert -> to right arrow
          copyright: "©", // Convert (c) to copyright
          trademark: "™", // Convert (tm) to trademark
          registeredTrademark: "®", // Convert (r) to registered
          oneHalf: "½", // Convert 1/2 to half
          oneQuarter: "¼", // Convert 1/4 to quarter
          threeQuarters: "¾", // Convert 3/4 to three quarters
          plusMinus: "±", // Convert +- to plus minus
          notEqual: "≠", // Convert != to not equal
          laquo: "«", // Convert << to left guillemet
          raquo: "»", // Convert >> to right guillemet
          multiplication: "×", // Convert x to multiplication
          superscriptTwo: "²", // Convert ^2 to squared
          superscriptThree: "³" // Convert ^3 to cubed
        }),
        ...(this.field?.props?.maxLength
          ? [
              CharacterCount.configure({
                limit: 999999 // Set a high character limit since we're enforcing byte limit separately
              }),
              Extension.create({
                name: "byteLengthLimit",
                addProseMirrorPlugins() {
                  const maxLength = (this.options as any).limit;

                  return [
                    new Plugin({
                      key: new PluginKey("byteLengthLimit"),
                      filterTransaction: (transaction, state) => {
                        // Allow transactions that don't change the document
                        if (!transaction.docChanged) {
                          return true;
                        }

                        // Get document before transaction
                        const oldJson = state.doc.toJSON();
                        const oldJsonString = JSON.stringify(oldJson);
                        const oldByteLength = new TextEncoder().encode(oldJsonString).length;

                        // Allow if under limit
                        if (oldByteLength < maxLength) {
                          return true;
                        }

                        // If we're at or over the limit, check if transaction reduces size
                        // Check the transaction steps to see if they're adding or removing content
                        let totalContentRemoved = 0;
                        let totalContentAdded = 0;

                        transaction.steps.forEach(step => {
                          const stepJSON = (step as any).toJSON?.();
                          if (stepJSON) {
                            // Check if content is being added
                            if (stepJSON.slice?.content) {
                              totalContentAdded += JSON.stringify(stepJSON.slice.content).length;
                            }
                            // Check if content is being removed (replace range with nothing or less)
                            if (stepJSON.from !== undefined && stepJSON.to !== undefined) {
                              const rangeSize = stepJSON.to - stepJSON.from;
                              if (rangeSize > 0) {
                                totalContentRemoved += rangeSize;
                              }
                            }
                          }
                        });

                        // Allow if we're removing more than adding (net reduction)
                        // This allows deletions, backspace, and clearContent operations
                        return totalContentRemoved >= totalContentAdded;
                      }
                    })
                  ];
                }
              }).configure({
                limit: this.field.props.maxLength
              } as any)
            ]
          : []),
        Image.configure({
          inline: true, // Back to inline
          allowBase64: false,
          HTMLAttributes: {
            class: "editor-image"
          }
        }),
        Mention.configure({
          HTMLAttributes: {
            class: "mention"
          },
          renderHTML({ node }) {
            return [
              "a",
              mergeAttributes(
                { class: "mention" },
                {
                  href: `/users/${node.attrs.id}/`,
                  "data-mention": node.attrs.id,
                  "data-username": node.attrs.id,
                  "data-display-name": node.attrs.label
                }
              ),
              `@${node.attrs.label}`
            ];
          },
          suggestion: {
            char: "@",
            startOfLine: false,
            allowSpaces: true,
            items: ({ query }) => {
              return new Promise(resolve => {
                // Allow queries of 2 or more characters
                if (query.length < 2) {
                  resolve([]);
                  return;
                }

                // Store the resolve function so we can clear it if needed
                this._currentMentionResolve = resolve;

                // Use the subject to trigger debounced search
                this._searchUsersSubject.next({
                  searchTerm: query,
                  renderList: resolve
                });
              });
            },
            render: () => {
              let component;
              let popup;
              let selectedIndex = 0;
              const windowRef = this._windowRef;

              return {
                onStart: props => {
                  // Always create a fresh component
                  component = document.createElement("div");
                  component.className = "mention-list";
                  selectedIndex = 0;
                },

                onUpdate: props => {
                  component.innerHTML = "";

                  // Remove popup if no items
                  if (props.items.length === 0) {
                    if (popup && popup.parentNode) {
                      popup.parentNode.removeChild(popup);
                      popup = null;
                    }
                    return;
                  }

                  // Create popup if it doesn't exist
                  if (!popup) {
                    popup = document.createElement("div");
                    popup.className = "tippy-box";
                    popup.setAttribute("data-theme", "mention");
                    popup.appendChild(component);
                    document.body.appendChild(popup);
                  }

                  // Position the popup
                  if (props.clientRect) {
                    const rect = props.clientRect();
                    popup.style.position = "absolute";
                    const scrollX = windowRef.nativeWindow ? windowRef.nativeWindow.scrollX : 0;
                    const scrollY = windowRef.nativeWindow ? windowRef.nativeWindow.scrollY : 0;
                    popup.style.left = `${rect.left + scrollX}px`;
                    popup.style.top = `${rect.bottom + scrollY + 2}px`;
                    popup.style.zIndex = "999";
                  }

                  props.items.forEach((item, index) => {
                    const element = document.createElement("button");
                    element.className = `mention-item user-mention ${index === selectedIndex ? "is-selected" : ""}`;
                    element.onclick = () =>
                      props.command({
                        id: item.username,
                        label: item.displayName
                      });

                    let avatarUrl: string | null = item.avatar;
                    if (!avatarUrl || avatarUrl.includes("default-avatar")) {
                      avatarUrl = Constants.DEFAULT_AVATAR;
                    }

                    const query = props.query || "";

                    // Since we're outside Angular's template system, we need to manually apply highlighting
                    const escapeHtml = (text: string): string => {
                      return text
                        .replace(/&/g, "&amp;")
                        .replace(/</g, "&lt;")
                        .replace(/>/g, "&gt;")
                        .replace(/"/g, "&quot;")
                        .replace(/'/g, "&#039;");
                    };

                    const highlightText = (text: string, searchQuery: string): string => {
                      if (!searchQuery || searchQuery.length < 2) {
                        return escapeHtml(text);
                      }

                      const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                      const regex = new RegExp(`(${escapedQuery})`, "gi");
                      const escapedText = escapeHtml(text);
                      return escapedText.replace(regex, '<span class="highlight">$1</span>');
                    };

                    const highlightedDisplayName = highlightText(item.displayName || item.username, query);
                    const highlightedUsername =
                      item.displayName && item.displayName !== item.username ? highlightText(item.username, query) : "";

                    element.innerHTML = `
                      <img class="avatar" src="${avatarUrl}" alt="${item.displayName}" />
                      <div class="mention-details">
                        <strong class="realname">${highlightedDisplayName}</strong>
                        ${item.displayName && item.displayName !== item.username ? `<span class="username">(${highlightedUsername})</span>` : ""}
                      </div>
                    `;

                    component.appendChild(element);
                  });

                  // Scroll selected item into view
                  const selectedElement = component.querySelector(".is-selected");
                  if (selectedElement) {
                    selectedElement.scrollIntoView({ block: "nearest" });
                  }
                },

                onKeyDown: ({ event }) => {
                  // Only handle keyboard events if popup exists
                  if (!popup || !component) {
                    return false;
                  }

                  if (event.key === "ArrowUp") {
                    const items = component.querySelectorAll(".mention-item");
                    if (items.length === 0) {
                      return false;
                    }
                    selectedIndex = (selectedIndex + items.length - 1) % items.length;

                    // Update selected class
                    items.forEach((item, index) => {
                      if (index === selectedIndex) {
                        item.classList.add("is-selected");
                      } else {
                        item.classList.remove("is-selected");
                      }
                    });

                    // Scroll into view
                    const selectedElement = component.querySelector(".is-selected");
                    if (selectedElement) {
                      selectedElement.scrollIntoView({ block: "nearest" });
                    }

                    return true;
                  }

                  if (event.key === "ArrowDown") {
                    const items = component.querySelectorAll(".mention-item");
                    if (items.length === 0) {
                      return false;
                    }
                    selectedIndex = (selectedIndex + 1) % items.length;

                    // Update selected class
                    items.forEach((item, index) => {
                      if (index === selectedIndex) {
                        item.classList.add("is-selected");
                      } else {
                        item.classList.remove("is-selected");
                      }
                    });

                    // Scroll into view
                    const selectedElement = component.querySelector(".is-selected");
                    if (selectedElement) {
                      selectedElement.scrollIntoView({ block: "nearest" });
                    }

                    return true;
                  }

                  if (event.key === "Enter") {
                    const selectedElement = component.querySelector(".mention-item.is-selected") as HTMLElement;
                    if (selectedElement) {
                      selectedElement.click();
                    }
                    return true;
                  }

                  if (event.key === "Escape") {
                    // Remove the popup when escape is pressed
                    if (popup && popup.parentNode) {
                      popup.parentNode.removeChild(popup);
                      popup = null;
                    }
                    return true;
                  }

                  return false;
                },

                onExit: () => {
                  if (popup && popup.parentNode) {
                    popup.parentNode.removeChild(popup);
                  }
                  popup = null;
                  component = null;
                  selectedIndex = 0;

                  // Clear any pending mention resolve
                  if (this._currentMentionResolve) {
                    this._currentMentionResolve([]);
                    this._currentMentionResolve = null;
                  }

                  // Force the editor to completely re-evaluate its state
                  if (this.editor) {
                    // Trigger a transaction to force TipTap to re-scan for mentions
                    const { state, view } = this.editor;
                    const tr = state.tr;
                    view.dispatch(tr);
                  }
                }
              };
            }
          }
        }),
        ...additionalExtensions
      ],
      content: this._getInitialContent(),
      editable: !this.readOnly,
      onUpdate: ({ editor }) => {
        // Skip if this update was triggered by a programmatic value change
        if (this._isUpdatingFromValueChange) {
          return;
        }

        const json = editor.getJSON();
        const html = editor.getHTML();
        const isEmpty = editor.isEmpty && !this._hasContent(editor);

        // Update character and byte counts
        if (this.field?.props?.maxLength) {
          this.currentCharacterCount = editor.storage.characterCount?.characters() || 0;
          const jsonString = isEmpty ? "" : JSON.stringify(json);
          this.currentByteCount = new TextEncoder().encode(jsonString).length;
          this._changeDetectorRef.markForCheck();
        }

        if (this.formControl) {
          // If the editor is empty, set empty string instead of the JSON with empty paragraph
          const value = isEmpty ? "" : JSON.stringify(json);

          // Set value and emit event so form valueChanges can trigger (e.g., for auto-save)
          this.formControl.setValue(value);
          this.formControl.markAsTouched();
          this.formControl.markAsDirty();

          // Validate byte count if maxLength is set
          if (this.field?.props?.maxLength) {
            const byteLength = new TextEncoder().encode(value).length;
            if (byteLength > this.field.props.maxLength) {
              this.formControl.setErrors({
                maxlength: { requiredLength: this.field.props.maxLength, actualLength: byteLength }
              });
            } else if (this.formControl.errors?.["maxlength"]) {
              // Clear the maxlength error if it exists and we're now under the limit
              const { maxlength, ...otherErrors } = this.formControl.errors;
              this.formControl.setErrors(Object.keys(otherErrors).length > 0 ? otherErrors : null);
            }
          }

          // Also mark the parent form as dirty
          if (this.form) {
            this.form.markAsDirty();
          }
        }

        // Check if content was cleared and select new placeholder
        if (isEmpty) {
          this._selectRandomPlaceholder();
          // Update the editor's placeholder
          this._updateEditorPlaceholder();
        }

        // Check if user is typing math delimiters
        if (!this._mathExtensionLoaded && !this._mathExtensionLoading) {
          const { from } = editor.state.selection;
          const recentText = editor.state.doc.textBetween(Math.max(0, from - 10), from);
          if (recentText.includes("$") || recentText.includes("\\(") || recentText.includes("\\[")) {
            void this._loadMathExtension();
          }
        }

        if (this.field?.props?.onContentChange) {
          // Ensure we're passing the correct values
          this.field.props.onContentChange({
            json: json, // This should be the JSON object
            html: html, // This should be the HTML string like "<p>test <strong>test</strong></p>"
            text: editor.getText() // Plain text content
          });
        }
      },
      onCreate: ({ editor }) => {
        this.isLoading = false;
        this._changeDetectorRef.markForCheck();

        // Initialize character and byte counts
        if (this.field?.props?.maxLength) {
          this.currentCharacterCount = editor.storage.characterCount?.characters() || 0;
          const json = editor.getJSON();
          const isEmpty = editor.isEmpty && !this._hasContent(editor);
          const jsonString = isEmpty ? "" : JSON.stringify(json);
          this.currentByteCount = new TextEncoder().encode(jsonString).length;
        }

        // Set initial value, considering empty state
        if (this.formControl) {
          const value = editor.isEmpty && !this._hasContent(editor) ? "" : JSON.stringify(editor.getJSON());
          this.formControl.setValue(value, { emitEvent: false });
        }

        // Trigger initial content change to set HTML
        if (this.field?.props?.onContentChange) {
          const json = editor.getJSON();
          const html = editor.getHTML();
          const text = editor.getText();
          this.field.props.onContentChange({ json, html, text });
        }

        // Call onEditorReady callback if provided
        if (this.field?.props?.onEditorReady) {
          this.field.props.onEditorReady(this);
        }

        // Check if content has math and load extension if needed
        const content = editor.state.doc.textContent;
        if (hasMathContent(content) && !this._mathExtensionLoaded) {
          void this._loadMathExtension();
        }

        // Subscribe to form control value changes
        this._subscribeToValueChanges();
      }
    });
  }

  private _subscribeToValueChanges(): void {
    if (this.formControl && this.editor) {
      this.formControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(value => {
        // Only update editor if the value is different from current content
        const currentValue = this.editor.isEmpty ? "" : JSON.stringify(this.editor.getJSON());
        if (value !== currentValue) {
          // Set flag to prevent circular updates
          this._isUpdatingFromValueChange = true;

          if (value && value !== "") {
            // Update with new content
            try {
              const content = JSON.parse(value);
              this.editor.commands.setContent(content);
            } catch (e) {
              // If not valid JSON, try as HTML
              this.editor.commands.setContent(value);
            }
          }

          // Reset flag after a micro-task to ensure the update is processed
          void Promise.resolve().then(() => {
            this._isUpdatingFromValueChange = false;
          });
        }
      });

      // Subscribe to form control status changes to handle disabled state
      this.formControl.statusChanges
        .pipe(
          takeUntil(this._destroyed$),
          debounceTime(0) // Use debounce to avoid infinite loops
        )
        .subscribe(status => {
          if (this.editor) {
            const shouldBeEditable = status !== "DISABLED";
            // Only update if the state actually changed
            if (this.editor.isEditable !== shouldBeEditable) {
              this.editor.setEditable(shouldBeEditable);
              this._changeDetectorRef.markForCheck();
            }
          }
        });
    }
  }

  private _getInitialContent(): any {
    const value = this.formControl?.value;
    if (!value) {
      return "";
    }

    // Try to parse as JSON first
    try {
      return JSON.parse(value);
    } catch (e) {
      // If it's not valid JSON, assume it's HTML and return as is
      // This handles backward compatibility with existing HTML content
      return value;
    }
  }

  private _selectRandomPlaceholder(): void {
    // Check if we have multiple placeholders
    const placeholders = this.field?.props?.placeholders;
    if (placeholders && Array.isArray(placeholders) && placeholders.length > 0) {
      // If we have more than one placeholder, ensure we select a different one
      if (placeholders.length > 1) {
        let newPlaceholder: string;
        do {
          const randomIndex = Math.floor(Math.random() * placeholders.length);
          newPlaceholder = placeholders[randomIndex];
        } while (newPlaceholder === this._currentPlaceholder && placeholders.length > 1);
        this._currentPlaceholder = newPlaceholder;
      } else {
        // Only one placeholder available
        this._currentPlaceholder = placeholders[0];
      }
    } else {
      // No placeholders array provided, use the single placeholder
      this._currentPlaceholder = this.field?.props?.placeholder || "";
    }
  }

  private _updateEditorPlaceholder(): void {
    if (this.editor) {
      // Update the placeholder extension configuration
      const placeholderExtension = this.editor.extensionManager.extensions.find(ext => ext.name === "placeholder");
      if (placeholderExtension) {
        placeholderExtension.options.placeholder = this._currentPlaceholder;
        // Force re-render by triggering an update
        this.editor.view.dispatch(this.editor.state.tr);
      }
    }
  }

  private _hasContent(editor: Editor): boolean {
    const html = editor.getHTML();
    // Check if editor has meaningful content (not just empty paragraph)
    return (
      editor.state.doc.content.size > 2 || // More than just empty paragraph
      html.includes('class="tiptap-math') || // Has math content
      html.includes('class="katex')
    ); // Has rendered math
  }

  private async _initializeEmojiPicker(): Promise<void> {
    if (!isPlatformBrowser(this._platformId) || this._emojiPicker) {
      return;
    }

    try {
      // Dynamically import emoji-picker-element to avoid SSR issues
      const { Picker } = await import("emoji-picker-element");

      // Load i18n based on current language
      let i18n;
      const currentLang = this.translateService.currentLang;

      switch (currentLang) {
        case "ar":
          i18n = await import("emoji-picker-element/i18n/ar");
          break;
        case "de":
          i18n = await import("emoji-picker-element/i18n/de");
          break;
        case "en":
          i18n = await import("emoji-picker-element/i18n/en");
          break;
        case "es":
          i18n = await import("emoji-picker-element/i18n/es");
          break;
        case "fr":
          i18n = await import("emoji-picker-element/i18n/fr");
          break;
        case "it":
          i18n = await import("emoji-picker-element/i18n/it");
          break;
        case "ja":
          i18n = await import("emoji-picker-element/i18n/ja");
          break;
        case "nl":
          i18n = await import("emoji-picker-element/i18n/nl");
          break;
        case "pl":
          i18n = await import("emoji-picker-element/i18n/pl");
          break;
        case "pt":
          // Check if it's Brazilian Portuguese
          if (currentLang === "pt" && this.translateService.currentLang === "pt_BR") {
            i18n = await import("emoji-picker-element/i18n/pt_BR");
          } else {
            i18n = await import("emoji-picker-element/i18n/pt_PT");
          }
          break;
        case "ru":
          i18n = await import("emoji-picker-element/i18n/ru_RU");
          break;
        case "tr":
          i18n = await import("emoji-picker-element/i18n/tr");
          break;
        case "vi":
          i18n = await import("emoji-picker-element/i18n/vi");
          break;
        case "zh":
        case "zh_Hans":
          i18n = await import("emoji-picker-element/i18n/zh_CN");
          break;
        // Languages not in emoji-picker: be, el, fi, hu, sq, sv, uk will use English
      }

      // Map our language codes to emoji data file names
      const languageMap: { [key: string]: string } = {
        ar: "en", // Arabic not available, use English
        be: "en", // Belarusian not available, use English
        bn: "bn",
        da: "da",
        de: "de",
        el: "en", // Greek not available, use English
        en: "en",
        es: "es",
        et: "et",
        fi: "fi",
        fr: "fr",
        hi: "hi",
        hu: "hu",
        it: "it",
        ja: "ja",
        ko: "ko",
        lt: "lt",
        ms: "ms",
        nb: "nb",
        nl: "nl",
        pl: "pl",
        pt: "pt",
        pt_BR: "pt", // Use pt for Brazilian Portuguese
        ru: "ru",
        sq: "en", // Albanian not available, use English
        sv: "sv",
        th: "th",
        tr: "en", // Turkish emoji data not downloaded, use English
        uk: "uk",
        vi: "vi",
        zh: "zh",
        zh_Hans: "zh"
      };

      const emojiDataLang = languageMap[currentLang] || "en";
      const dataSource = `/assets/emojis-${emojiDataLang}-20250606.json`;

      const pickerOptions: any = {
        dataSource: dataSource,
        skinToneEmoji: "🖐️"
      };

      if (i18n) {
        pickerOptions.i18n = i18n.default || i18n;
      }

      this._emojiPicker = new Picker(pickerOptions);

      // Handle emoji selection
      this._emojiPicker.addEventListener("emoji-click", (event: any) => {
        if (this.editor && event.detail?.unicode) {
          this.editor.chain().focus().insertContent(event.detail.unicode).run();
          this.showEmojiPicker = false;
          this._changeDetectorRef.markForCheck();
        }
      });

      // Append to the picker element after next tick
      this.utilsService.delay(1).subscribe(() => {
        if (this.emojiPickerElement?.nativeElement && this._emojiPicker) {
          this.emojiPickerElement.nativeElement.appendChild(this._emojiPicker);
        }
      });
    } catch (error) {
      console.error("Failed to initialize emoji picker:", error);
    }
  }

  private _insertImage(url: string, fileName: string, imageLink?: string): void {
    if (!this.editor) {
      return;
    }

    // Always include the filename as a caption
    const imgHtml = `<img src="${url}" alt="${fileName}" title="${fileName}" />`;
    let contentWithCaption = `<strong>📷 ${fileName}</strong>${imgHtml}`;

    // Add link if provided (for gallery images)
    if (imageLink) {
      contentWithCaption += `<p><a href="${imageLink}" target="_blank">${imageLink}</a></p>`;
    }

    // Use the same approach for both empty and non-empty editors
    this.editor.chain().focus().insertContent(contentWithCaption).run();

    // Move cursor to end
    this.editor.commands.focus("end");
  }

  private async _handleDroppedFile(file: File): Promise<void> {
    if (!this.editor) {
      return;
    }

    // Clear any existing upload error notification
    if (this._uploadErrorToastId) {
      this._popNotificationsService.remove(this._uploadErrorToastId);
      this._uploadErrorToastId = undefined;
    }

    // Focus the editor first to prevent validation issues
    this.editor.commands.focus();

    this.isUploading = true;

    // If editor is empty, add invisible character to prevent validation error
    const wasEmpty = this.editor.isEmpty;
    if (wasEmpty) {
      this.editor.commands.setContent("\u200B"); // Zero-width space
    }

    this.editor.setEditable(false); // Disable editor during upload
    this._changeDetectorRef.markForCheck();

    try {
      const result = await firstValueFrom(this._commonApiService.editorUpload(file));
      if (result.url) {
        if (wasEmpty) {
          this.editor.commands.clearContent();
        }

        this.editor.commands.focus();

        // Check if it's an image
        if (file.type.startsWith("image/")) {
          this._insertImage(result.url, file.name);
        } else {
          // For non-image files, insert as a link
          const linkText = file.name;
          this.editor
            .chain()
            .focus()
            .insertContent(
              `<a href="${result.url}" target="_blank" rel="noopener noreferrer" class="tiptap-link">${linkText}</a>`
            )
            .run();
        }
      }
    } catch (error: any) {
      // Show the error message (already localized from backend or fallback)
      const toast = this._popNotificationsService.error(error.message);
      if (toast) {
        this._uploadErrorToastId = toast.toastId;
      }

      if (wasEmpty) {
        this.editor.commands.clearContent();
      }
    } finally {
      this.isUploading = false;
      this.editor.setEditable(true); // Re-enable editor after upload
      this._changeDetectorRef.markForCheck();
    }
  }

  private _setupDebouncedUserSearch(): void {
    this._searchUsersSubscription = this._searchUsersSubject
      .pipe(
        debounceTime(100),
        switchMap(({ searchTerm, renderList }) => {
          const token = this._authService.getClassicApiToken();
          const url = `${environment.classicBaseUrl}/autocomplete_usernames/?q=${encodeURIComponent(searchTerm)}&token=${token}`;

          return this._http.get<any[]>(url).pipe(
            map(response => ({ response, renderList })),
            catchError(() => {
              renderList([]);
              return EMPTY;
            })
          );
        })
      )
      .subscribe(({ response, renderList }) => {
        const users = response.map(user => ({
          id: user.id,
          username: user.username,
          displayName: user.displayName || user.username,
          realName: user.realName || "",
          avatar: user.avatar || "/assets/images/default-avatar.jpeg"
        }));

        renderList(users);
      });
  }

  private _cleanPastedHtml(html: string): string {
    // Use the ForumHtmlSanitizerService to sanitize the HTML
    const safeHtml = this._forumHtmlSanitizer.sanitizeHtml(html);

    // Extract the string from SafeHtml
    // Since ForumHtmlSanitizerService uses bypassSecurityTrustHtml,
    // we need to get the actual string value
    return (safeHtml as any).changingThisBreaksApplicationSecurity || safeHtml.toString();
  }

  private async _loadMathExtension(): Promise<void> {
    if (this._mathExtensionLoaded || this._mathExtensionLoading) {
      return;
    }

    this._mathExtensionLoading = true;

    try {
      const mathExtension = await loadMathExtension();
      this._mathExtension = mathExtension; // Store the extension

      // If editor exists, recreate it with math extension
      if (this.editor) {
        const content = this.editor.getJSON();
        // Store reference to nativeElement (no longer needed but kept for clarity)

        // Destroy current editor
        this.editor.destroy();

        // Mark as loaded before creating new editor to avoid duplicate extensions
        this._mathExtensionLoaded = true;

        // Create new editor with math extension
        await this._initializeEditorWithExtensions([mathExtension]);

        // Restore content
        if (content) {
          this.editor.commands.setContent(content);
        }
      } else {
        // Editor not yet created, just mark as loaded
        this._mathExtensionLoaded = true;
      }
    } catch (error) {
      console.error("Failed to load math extension:", error);
      this._popNotificationsService.error(
        this.translateService.instant("Failed to load math support. Please try again.")
      );
    } finally {
      this._mathExtensionLoading = false;
    }
  }

  /**
   * Extract text content from various content formats
   */
  private _extractTextFromContent(content: any): string {
    if (!content) {
      return "";
    }

    if (typeof content === "string") {
      return content;
    }

    if (content.type === "doc" && content.content) {
      // Recursively extract text from JSON content
      return this._extractTextFromNodes(content.content);
    }

    return "";
  }

  /**
   * Recursively extract text from TipTap nodes
   */
  private _extractTextFromNodes(nodes: any[]): string {
    let text = "";

    for (const node of nodes) {
      if (node.type === "text") {
        text += node.text || "";
      } else if (node.type === "inlineMath" && node.attrs?.latex) {
        // Include math delimiters in text
        text += `$${node.attrs.latex}$`;
      } else if (node.type === "displayMath" && node.attrs?.latex) {
        text += `$$${node.attrs.latex}$$`;
      } else if (node.content) {
        text += this._extractTextFromNodes(node.content);
      }
      text += " ";
    }

    return text;
  }

  /**
   * Check if content has math nodes (inlineMath or displayMath)
   */
  private _contentHasMathNodes(content: any): boolean {
    if (!content) {
      return false;
    }

    if (content.type === "doc" && content.content) {
      return this._nodesHaveMath(content.content);
    }

    return false;
  }

  /**
   * Recursively check if nodes contain math
   */
  private _nodesHaveMath(nodes: any[]): boolean {
    for (const node of nodes) {
      if (node.type === "inlineMath" || node.type === "displayMath") {
        return true;
      }
      if (node.content && this._nodesHaveMath(node.content)) {
        return true;
      }
    }
    return false;
  }
}
